import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '../styles/driverTour.css'
import api from '../utils/api'

const TOUR_COMPLETED_KEY = 'beauty_business_app_tour_completed'
const TOUR_PENDING_KEY = 'beauty_business_app_tour_pending'

export { TOUR_COMPLETED_KEY, TOUR_PENDING_KEY }

/**
 * 系統教學導覽步驟：從首頁（預約管理）開始，引導至設定頁 → 店家資訊、預約須知、取消規定 → 員工列表 → 員工詳情頁（頭像、姓名、工作時間、休假、作品集、個人經歷）
 * @param {boolean} isMobile
 * @param {string} [businessId] - 有值時，員工步驟會導向詳情頁並圈選該頁元素
 * @param {string} [employeeId] - 有值時，員工步驟會導向 /employees/:businessId/:employeeId
 */
function buildSteps(isMobile, businessId, employeeId) {
  const firstStepSelector = isMobile
    ? '[data-tour="mobile-menu"]'
    : '[data-tour="sidebar"]'
  const firstStepHint = isMobile
    ? '請點擊左上角選單，在選單中找到「設定」開始。'
    : '請從左側側邊欄點擊「設定」開始，我們將帶您到設定頁。'

  const onDetailPage = !!(businessId && employeeId)
  const employeeSteps = onDetailPage
    ? [
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '員工管理',
            description:
              '請點擊下方任一員工（員工姓名）進入編輯頁面，點擊「下一步」我們將帶您前往該頁面繼續教學。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-avatar"]',
          popover: {
            title: '頭像與員工姓名',
            description: '在此上傳頭像、填寫員工姓名，讓客戶認識您的團隊。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-name"]',
          popover: {
            title: '員工姓名',
            description: '填寫員工姓名，顯示於預約與店家頁面。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-work-hours-tab"]',
          popover: {
            title: '工作時間',
            description:
              '點擊「工作時間」分頁，設定每位員工的可預約時段，客戶才能選擇該時段預約。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-days-off-tab"]',
          popover: {
            title: '休假',
            description:
              '點擊「休假」分頁，設定休假日或區間，系統會自動排除這些時段。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-portfolio-tab"]',
          popover: {
            title: '作品集',
            description:
              '點擊「作品集」分頁：請先新增分類（標籤），再新增作品並上傳照片。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-experience-tab"]',
          popover: {
            title: '個人經歷',
            description:
              '點擊「個人經歷」分頁，填寫工作經歷，讓客戶更了解專業背景。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employee-detail"]',
          popover: {
            title: '恭喜完成設定！',
            description:
              '您已經成功建立好自己的個人頁面了！請繼續探索剩下的功能吧！',
            side: 'top',
            align: 'center',
            doneBtnText: '完成'
          }
        }
      ]
    : [
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '員工管理',
            description:
              '在此可新增、編輯員工。請點擊任一員工進入編輯，完成以下設定。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '頭像與員工姓名',
            description:
              '在員工編輯頁的「員工資訊」分頁：上傳頭像、填寫員工姓名，讓客戶認識您的團隊。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '工作時間',
            description:
              '在「工作時間」分頁設定每位員工的可預約時段，客戶才能選擇該時段預約。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '休假',
            description:
              '在「休假」分頁設定休假日或區間，系統會自動排除這些時段，避免客戶預約到不可用時間。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '作品集',
            description:
              '在「作品集」分頁：請先新增分類（標籤），再新增作品並上傳照片，讓客戶看到每位員工的風格與作品。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '個人經歷',
            description:
              '在「個人經歷」分頁填寫工作經歷，讓客戶更了解每位員工的專業背景。',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="employees-intro"]',
          popover: {
            title: '恭喜完成設定！',
            description:
              '您已經成功建立好自己的個人頁面了！請繼續探索剩下的功能吧！',
            side: 'bottom',
            align: 'start',
            doneBtnText: '完成'
          }
        }
      ]

  return [
    {
      element: firstStepSelector,
      popover: {
        title: '歡迎使用系統教學',
        description: `您目前在首頁（預約管理）。我們將引導您到設定頁，認識店家資訊、預約規則與員工設定。${firstStepHint}`,
        side: isMobile ? 'bottom' : 'right',
        align: 'start'
      }
    },
    {
      element: '[data-tour="store-name"]',
      popover: {
        title: '店家名稱',
        description: '在此編輯商店名稱，讓客戶一眼認出您的店家。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="store-photos"]',
      popover: {
        title: '上傳照片',
        description: '上傳店家或作品照片，最多 5 張，有助客戶認識您的風格。',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '[data-tour="store-description"]',
      popover: {
        title: '店家介紹',
        description: '填寫店家介紹與服務說明，讓預約者更了解您。',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '[data-tour="booking-instructions"]',
      popover: {
        title: '預約須知',
        description: '設定預約規則與注意事項，客戶預約前會看到此內容。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="cancellation-policy"]',
      popover: {
        title: '取消規定',
        description: '設定取消與改期規則，避免爭議。',
        side: 'bottom',
        align: 'start'
      }
    },
    ...employeeSteps
  ]
}

function getStepPaths(businessId, employeeId) {
  return [
    '', // 步驟 0：首頁
    '/settings/info',
    '/settings/info',
    '/settings/info',
    '/settings/info',
    '/settings/booking',
    '/settings/cancel',
    '/employees', // 員工管理（列表頁）
    ...(businessId && employeeId
      ? Array(8).fill(`/employees/${businessId}/${employeeId}`)
      : Array(7).fill('/employees'))
  ]
}

/**
 * 啟動系統教學導覽。支援跨頁：下一步若為不同 path 會先 navigate 再顯示該步。
 * 若傳入 employeeId，員工步驟會導向該員工詳情頁並在該頁圈選頭像、分頁等元素。
 * @param {Function} navigate - react-router navigate
 * @param {number} startStepIndex - 從第幾步開始（0 為側邊欄/選單）
 * @param {string} [businessId] - 店家 ID，用於呼叫標記完成 API 與組員工詳情路徑
 * @param {string} [employeeId] - 員工 ID，有值時會導向 /employees/:businessId/:employeeId 做教學
 */
export function startAppTour(
  navigate,
  startStepIndex = 0,
  businessId,
  employeeId
) {
  const getCurrentPath = () => window.location.pathname || ''
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 820
  const steps = buildSteps(isMobile, businessId, employeeId)
  const stepPaths = getStepPaths(businessId, employeeId)

  const driverObj = driver({
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    nextBtnText: '下一步',
    prevBtnText: '上一步',
    doneBtnText: '完成',
    overlayColor: '#000',
    overlayOpacity: 0.75,
    stagePadding: 8,
    stageRadius: 8,
    allowClose: true,
    popoverClass: 'beauty-app-tour-popover',
    steps,
    onNextClick: (element, step, opts) => {
      const nextIndex = (opts.state?.activeIndex ?? 0) + 1
      if (nextIndex >= steps.length) {
        driverObj.destroy()
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
        }
        return
      }
      const nextPath = stepPaths[nextIndex]
      const currentPath = getCurrentPath()
      if (nextPath && nextPath !== currentPath) {
        navigate(nextPath)
        setTimeout(() => {
          driverObj.drive(nextIndex)
        }, 500)
      } else {
        driverObj.moveNext()
      }
    },
    onDestroyed: () => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
      }
      if (businessId) {
        api
          .post(`/api/businesses/${businessId}/onboarding/complete`, {})
          .catch(() => {})
      }
    }
  })

  const currentPath = getCurrentPath()
  const requiredPath = stepPaths[startStepIndex]
  if (requiredPath && requiredPath !== currentPath) {
    navigate(requiredPath)
    setTimeout(() => driverObj.drive(startStepIndex), 500)
  } else {
    driverObj.drive(startStepIndex)
  }
  return driverObj
}

/** 是否已完成過系統教學 */
export function isAppTourCompleted() {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(TOUR_COMPLETED_KEY) === 'true'
}

/** 是否待進行教學（手動設為 pending 時使用） */
export function isAppTourPending() {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(TOUR_PENDING_KEY) === 'true'
}

/** 設為待進行（進入主畫面後會導向設定並開始教學） */
export function setAppTourPending() {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(TOUR_PENDING_KEY, 'true')
  }
}

/** 清除待進行與完成標記（可重新跑一次教學） */
export function resetAppTour() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(TOUR_COMPLETED_KEY)
    window.localStorage.removeItem(TOUR_PENDING_KEY)
  }
}

// --- 抽成設定教學（與店家設定同套件 driver.js）---

const COMMISSION_TOUR_STEP_PATHS = [
  '/commission/performance',
  '/commission/performance',
  '/commission/performance',
  '/commission/performance',
  '/commission/service-rules',
  '/commission/service-rules',
  '/commission/service-rules'
]

function buildCommissionSteps() {
  return [
    {
      element: '[data-tour="commission-employee-select"]',
      popover: {
        title: '選擇員工',
        description:
          '在此選擇要設定業績抽成的員工，可輸入姓名搜尋。選完後系統會自動載入該員工目前的階梯抽成設定。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="commission-deduct-material"]',
      popover: {
        title: '抽成計算方式',
        description:
          '「先扣材料費再算抽成」僅在該服務沒有服務項目抽成規則時生效，公式：(服務業績 − 材料費) × 階梯趴數。若服務已有服務規則，以該規則為準。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="commission-tiers"]',
      popover: {
        title: '階梯抽成',
        description:
          '依序設定「大於等於（門檻）」與「抽成趴數」，建議由低至高。例如：0→30%、50000→35%、100000→40%。需要更多層級可點「新增抽成階梯」。',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '[data-tour="commission-save"]',
      popover: {
        title: '保存設定',
        description: '確認門檻與趴數後，點擊「保存設定」。更新後可依需要選擇是否重新計算當月薪資。',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '[data-tour="service-rules-intro"]',
      popover: {
        title: '服務項目抽成規則',
        description:
          '為每個服務設定獨立的抽成規則，支援一般抽成或第一劑/第二劑模式。有設定本規則的服務以本規則為準，未設定的才套用員工階梯抽成。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="service-rules-search"]',
      popover: {
        title: '搜尋與展開服務',
        description:
          '可搜尋服務名稱或依分類篩選。點擊下方任一服務項目展開，即可設定該服務的抽成規則（啟用規則、抽成類型、設計師/助理趴數等）。',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '[data-tour="service-rules-intro"]',
      popover: {
        title: '抽成設定教學完成',
        description:
          '您已認識技術服務業績抽成與服務項目抽成規則的設定方式。記得：服務規則優先，未設定的服務才套用員工階梯抽成。',
        side: 'bottom',
        align: 'start',
        doneBtnText: '完成'
      }
    }
  ]
}

/**
 * 啟動抽成設定教學導覽（driver.js，與店家設定同一套件）。
 * 從技術服務業績抽成頁開始，依序介紹選擇員工、抽成計算方式、階梯、保存，再導向服務項目抽成規則頁。
 * @param {Function} navigate - react-router navigate
 * @param {number} startStepIndex - 從第幾步開始（0 為選擇員工）
 */
export function startCommissionTour(navigate, startStepIndex = 0) {
  const getCurrentPath = () => window.location.pathname || ''
  const steps = buildCommissionSteps()
  const stepPaths = COMMISSION_TOUR_STEP_PATHS

  const driverObj = driver({
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    nextBtnText: '下一步',
    prevBtnText: '上一步',
    doneBtnText: '完成',
    overlayColor: '#000',
    overlayOpacity: 0.75,
    stagePadding: 8,
    stageRadius: 8,
    allowClose: true,
    popoverClass: 'beauty-app-tour-popover',
    steps,
    onNextClick: (element, step, opts) => {
      const nextIndex = (opts.state?.activeIndex ?? 0) + 1
      if (nextIndex >= steps.length) {
        driverObj.destroy()
        return
      }
      const nextPath = stepPaths[nextIndex]
      const currentPath = getCurrentPath()
      if (nextPath && nextPath !== currentPath) {
        navigate(nextPath)
        setTimeout(() => {
          driverObj.drive(nextIndex)
        }, 500)
      } else {
        driverObj.moveNext()
      }
    }
  })

  const currentPath = getCurrentPath()
  const requiredPath = stepPaths[startStepIndex]
  if (requiredPath && requiredPath !== currentPath) {
    navigate(requiredPath)
    setTimeout(() => driverObj.drive(startStepIndex), 500)
  } else {
    driverObj.drive(startStepIndex)
  }
  return driverObj
}
